import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Button,
  Empty,
  Pagination,
  Progress,
  Select,
  Spin,
  Tooltip,
} from "antd";
import { AlertOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { httpService } from "../../../../services/httpService.service";
import {
  getTaskManagerHome,
  escalateStalledTasks,
} from "../../../../services/features/dashboard.service";
import { RolesEnum } from "../../../../services/rolesEnum";
import { isAuthorized } from "../../../../services/authService";
import MessagePopUp from "../../../components/Messages/MessagePopUp";
import StalledPill from "../../../components/StalledPill/StalledPill";
import doneIcon from "../../../../Assets/svg/done.svg";
import errorIcon from "../../../../Assets/svg/exclamationMark.svg";
import { taskTypes, taskStatusesEnum } from "../../../../services/Enums/taskStatusesEnum";
import { formatDate } from "../../../../services/helpFunctions/formatDateAndTime";
import { HOME_COLORS, muted } from "../homeTheme";
import {
  isActiveTask,
  sortTasksByUrgency,
  groupTasksByVisit,
  getTaskUrgency,
  computeVisitCompletion,
} from "../../../../services/helpFunctions/taskUrgency";

const PAGE_SIZE = 10;

// The status enum's `fill` may be a CSS var() token (e.g. "var(--x, #FC9403)"),
// which canvas can't resolve — pull the raw hex out for chart colours.
const hexOf = (fill) => {
  const m = /#([0-9a-fA-F]{3,8})/.exec(fill || "");
  return m ? m[0] : fill || "#555555";
};

// F-02 — Task manager as the project home center.
// `mode="personal"` shows the current user's active queue; `mode="team"` shows
// every assignee's tasks (including completed, in green) with assignee + status
// filters and pagination (used on the PM home).
const TaskQueuePanel = ({ mode = "personal" }) => {
  const navigate = useNavigate();
  const projectID = localStorage.getItem("currentProjectID");
  const userID = localStorage.getItem("userID");

  const [allTasks, setAllTasks] = useState([]); // every project task (for visit %)
  const [myTasks, setMyTasks] = useState([]); // current user's tasks (personal mode)
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const [msg, setMsg] = useState({ trigger: false, type: "", icon: "", title: "", text: "" });
  const [assigneeFilter, setAssigneeFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [stalledOnly, setStalledOnly] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [page, setPage] = useState(1);

  // Automated Status Escalation is a project-wide, PM/Admin action.
  const canEscalate = isAuthorized([
    RolesEnum.ProjectManager,
    RolesEnum.SystemAdmin,
  ]);

  const fetchTasks = useCallback(() => {
    if (!projectID) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // F-02 — the personal queue now comes from the dedicated Task Manager home
    // endpoint, which is already priority-sorted (overdue first) and carries a
    // per-task `nextBestAction`, the F-04 `siteVisitProgress`, and the stalled
    // flag. Personal mode uses the endpoint's server-side Overdue/Stalled
    // filters; team mode (PM) still reads every project task (filtered client
    // side) since there's no dedicated team endpoint.
    if (mode === "personal") {
      getTaskManagerHome(
        {
          projectId: projectID,
          overdueOnly: overdueOnly || undefined,
          isStalled: stalledOnly || undefined,
        },
        (res) => {
          const items = res?.data || res || [];
          // Flatten each { task, nextBestAction, isOverdue } into the task,
          // tagging it so the render keeps working unchanged.
          const tasks = (Array.isArray(items) ? items : []).map((it) => ({
            ...(it?.task || {}),
            _nextBestAction: it?.nextBestAction,
            _isOverdue: it?.isOverdue,
          }));
          setMyTasks(tasks);
          setLoading(false);
        },
        () => setLoading(false)
      );
      return;
    }

    // Team mode — all project tasks, used to compute each visit's completion %.
    httpService.get(
      `/api/Tasks/getAllTasks?ProjectId=${projectID}&pageSize=1000&pageNumber=1`,
      (res) => {
        setAllTasks(res?.data || []);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectID, userID, mode, overdueOnly, stalledOnly]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // F-04 — visit completion. Personal mode uses each task's server-provided
  // siteVisitProgress; team mode computes it from the full project task set.
  const visitCompletion = useMemo(() => {
    if (mode === "personal") {
      const map = new Map();
      myTasks.forEach((t) => {
        const visitId = t?.taskSite?.id;
        const p = t?.siteVisitProgress;
        if (visitId && p) {
          map.set(visitId, {
            total: p.totalTasks,
            completed: p.completedTasks,
            percent: Math.round(p.percentage || 0),
          });
        }
      });
      return map;
    }
    return computeVisitCompletion(allTasks);
  }, [mode, myTasks, allTasks]);

  // Distinct assignees present across project tasks (for the PM team filter).
  const assigneeOptions = useMemo(() => {
    const map = new Map();
    allTasks.forEach((t) =>
      (t?.assignees || []).forEach((a) => {
        if (a?.id && !map.has(a.id)) {
          const name = [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email || "User";
          map.set(a.id, name);
        }
      })
    );
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [allTasks]);

  // Personal: active tasks only. Team: all statuses (so completed shows green),
  // honouring assignee/status filters. Active tasks first by urgency, finished
  // (completed/archived) last.
  const sortedTasks = useMemo(() => {
    let source = mode === "personal" ? myTasks : allTasks;
    if (mode === "personal") {
      // Overdue/Stalled are already applied server-side in personal mode.
      source = source.filter(isActiveTask);
    } else {
      if (assigneeFilter)
        source = source.filter((t) => (t?.assignees || []).some((a) => a.id === assigneeFilter));
      if (statusFilter) source = source.filter((t) => Number(t?.status) === Number(statusFilter));
      if (overdueOnly)
        source = source.filter(
          (t) => isActiveTask(t) && getTaskUrgency(t).category === "overdue"
        );
      if (stalledOnly) source = source.filter((t) => t?.isStalled);
    }
    const active = source.filter(isActiveTask);
    const finished = source.filter((t) => !isActiveTask(t));
    return [...sortTasksByUrgency(active), ...sortTasksByUrgency(finished)];
  }, [mode, myTasks, allTasks, assigneeFilter, statusFilter, overdueOnly, stalledOnly]);

  // Analytics over the loaded task set (post server-side filters in personal
  // mode; the full project set in team mode).
  const analyticsSource = mode === "personal" ? myTasks : allTasks;
  const stats = useMemo(() => {
    const byStatus = {};
    let overdue = 0;
    let stalled = 0;
    let dueThisWeek = 0;
    let completed = 0;
    const startOfToday = dayjs().startOf("day");
    const weekEnd = dayjs().add(7, "day").endOf("day");
    analyticsSource.forEach((t) => {
      const s = Number(t?.status);
      byStatus[s] = (byStatus[s] || 0) + 1;
      if (s === 3) completed += 1;
      if (t?.isStalled) stalled += 1;
      if (isActiveTask(t)) {
        if (getTaskUrgency(t).category === "overdue") overdue += 1;
        if (t?.deadline) {
          const d = dayjs(t.deadline);
          if (d.isAfter(startOfToday) && d.isBefore(weekEnd)) dueThisWeek += 1;
        }
      }
    });
    const total = analyticsSource.length;
    return {
      byStatus,
      overdue,
      stalled,
      dueThisWeek,
      completed,
      total,
      completionPct: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [analyticsSource]);

  // Status mix for the stacked bar: label, count and a muted colour per status,
  // biggest slice first so the bar reads left to right.
  const statusMix = useMemo(
    () =>
      Object.keys(stats.byStatus)
        .map((key) => {
          const status = taskStatusesEnum[key] || taskStatusesEnum.default;
          return {
            key,
            label: status.text,
            count: stats.byStatus[key],
            color: muted(hexOf(status.fill)),
          };
        })
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count),
    [stats]
  );

  const handleEscalate = () => {
    setEscalating(true);
    escalateStalledTasks(
      () => {
        setEscalating(false);
        notify(
          "success",
          "Stalled tasks refreshed",
          "Tasks have been re-evaluated for stalls."
        );
        fetchTasks();
      },
      () => {
        setEscalating(false);
        notify("danger", "Refresh failed", "Couldn't refresh stalled tasks.");
      }
    );
  };

  // Reset to first page whenever the filtered set changes.
  useEffect(() => {
    setPage(1);
  }, [assigneeFilter, statusFilter, overdueOnly, stalledOnly, mode]);

  // Clamp the page if the list shrank (e.g. after completing a task).
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [sortedTasks, page]);

  const pagedTasks = useMemo(
    () => sortedTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedTasks, page]
  );
  const groups = useMemo(() => groupTasksByVisit(pagedTasks), [pagedTasks]);
  const nextAction = useMemo(() => sortedTasks.find(isActiveTask), [sortedTasks]);

  // F-02 — "what to do next". Personal mode prefers the backend's plain-language
  // nextBestAction prompt; team mode falls back to the most urgent task name.
  const nextActionText = useMemo(() => {
    if (mode === "personal") {
      const withPrompt = sortedTasks.find((t) => t?._nextBestAction);
      if (withPrompt?._nextBestAction) return withPrompt._nextBestAction;
    }
    if (!nextAction) return null;
    return `${nextAction.name || nextAction.identifier}${
      nextAction?.taskSite?.name ? ` — ${nextAction.taskSite.name}` : ""
    }`;
  }, [mode, sortedTasks, nextAction]);

  const notify = (type, title, text) => {
    setMsg({
      trigger: true,
      type,
      icon: type === "success" ? doneIcon : errorIcon,
      title,
      text,
    });
    setTimeout(() => setMsg((m) => ({ ...m, trigger: false })), 3000);
  };

  const handleComplete = (task) => {
    setCompletingId(task.id);
    httpService.put(
      `/api/Tasks/updateTaskStatus`,
      { id: task.id, status: 3 },
      () => {
        setCompletingId(null);
        notify("success", "Task completed", `"${task.name || "Task"}" marked complete.`);
        fetchTasks(); // refresh queue + linked visit progress, no navigation
      },
      () => {
        setCompletingId(null);
        notify("danger", "Couldn't complete", "Couldn't complete task. Please try again.");
      }
    );
  };

  const renderTask = (task) => {
    const urgency = getTaskUrgency(task);
    const status = taskStatusesEnum[task.status] || taskStatusesEnum.default;
    const active = isActiveTask(task);
    const stalledDays = task.stalledSince
      ? dayjs().diff(dayjs(task.stalledSince), "day")
      : null;
    return (
      <div className="homeTaskRow" key={task.id}>
        <div className="homeTaskMain">
          <span
            className="homeTaskName"
            onClick={() => navigate("/project/planning?tasks")}
            title="Open in Tasks board"
          >
            {task.name || task.identifier || "Task"}
          </span>
          <div className="homeTaskMeta">
            {/* Status — same tinted-pill design as the Tasks table */}
            <div className={status.className}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8px"
                height="8px"
                viewBox="0 0 9 9"
              >
                <circle cx="4.5" cy="4.27344" r="4" fill={status.fill} />
              </svg>
              <span>{status.text}</span>
            </div>
            <span className="homePill homePill--type">
              {taskTypes[task.type] || "Task"}
            </span>
            {task.isStalled && (
              <StalledPill stalledSince={task.stalledSince} days={stalledDays} />
            )}
            {active && (
              <span className={`homePill homePill--${urgency.category}`}>
                {urgency.label}
                {task.deadline ? ` · ${formatDate(task.deadline)}` : ""}
              </span>
            )}
            {mode === "team" &&
              (task.assignees || []).slice(0, 1).map((a) => (
                <span className="homeTaskAssignee" key={a.id}>
                  {[a.firstName, a.lastName].filter(Boolean).join(" ") || a.email}
                </span>
              ))}
          </div>
        </div>
        {active && (
          <Button
            size="small"
            className="inviteButtonTPM homeTaskComplete"
            loading={completingId === task.id}
            onClick={() => handleComplete(task)}
          >
            Mark complete
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="homePanel">
        <MessagePopUp
          trigger={msg.trigger}
          type={msg.type}
          icon={msg.icon}
          messageTitle={msg.title}
          messageText={msg.text}
        />
        <div className="homePanelLoading">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="homePanel">
      <MessagePopUp
        trigger={msg.trigger}
        type={msg.type}
        icon={msg.icon}
        messageTitle={msg.title}
        messageText={msg.text}
      />
      <div className="homePanelHead">
        <h3>{mode === "personal" ? "Your tasks" : "Team tasks"}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="homePanelCount">
            {sortedTasks.length} {mode === "personal" ? "active" : "tasks"}
          </span>
          {canEscalate && (
            <Tooltip title="Re-check every task for stalls and flag the ones with no recent update">
              <Button
                icon={<AlertOutlined />}
                loading={escalating}
                onClick={handleEscalate}
                className="inviteButtonTPM homeEscalateBtn"
              >
                Escalate stalled
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Analytics — the status mix as one 8px stacked bar with its counts
          spelled out underneath, plus the four numbers that drive action. No
          canvas, no legend to decode, nothing behind a hover. */}
      {stats.total > 0 && (
        <div className="homeTaskAnalytics">
          <div className="homeTaskMix">
            <div className="homeTaskMixBar">
              {statusMix.map((s) => (
                <Tooltip
                  key={s.key}
                  title={`${s.label}: ${s.count} of ${stats.total}`}
                >
                  <span
                    className="homeTaskMixSeg"
                    style={{
                      width: `${(s.count / stats.total) * 100}%`,
                      background: s.color,
                    }}
                  />
                </Tooltip>
              ))}
            </div>
            <div className="homeTaskMixLegend">
              {statusMix.map((s) => (
                <span className="homeTaskMixItem" key={s.key}>
                  <span className="homeDot" style={{ background: s.color }} />
                  {s.label} <strong>{s.count}</strong>
                </span>
              ))}
            </div>
          </div>
          <div className="homeTaskStats">
            {[
              { label: "Overdue", value: stats.overdue, color: HOME_COLORS.alert },
              { label: "Stalled", value: stats.stalled, color: HOME_COLORS.stalled },
              { label: "Due this week", value: stats.dueThisWeek, color: HOME_COLORS.warn },
              { label: "Completed", value: `${stats.completionPct}%`, color: HOME_COLORS.ok },
            ].map((s) => (
              <div className="homeTaskStat" key={s.label}>
                <span className="homeTaskStatValue">{s.value}</span>
                <span className="homeTaskStatLabel">
                  <span className="homeDot" style={{ background: s.color }} />
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overdue / Stalled quick toggles (both modes) */}
      <div className="homeTaskToggles">
        <Button
          className={`homeToggleBtn ${overdueOnly ? "homeToggleActive" : ""}`}
          onClick={() => setOverdueOnly((v) => !v)}
        >
          Overdue only
        </Button>
        <Button
          className={`homeToggleBtn ${stalledOnly ? "homeToggleActive" : ""}`}
          onClick={() => setStalledOnly((v) => !v)}
        >
          Stalled only
        </Button>
      </div>

      {mode === "team" && (
        <div className="homeTeamFilters">
          <Select
            allowClear
            showSearch
            placeholder="Filter by assignee"
            style={{ minWidth: 200 }}
            value={assigneeFilter}
            onChange={setAssigneeFilter}
            optionFilterProp="label"
            options={assigneeOptions}
          />
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ minWidth: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 1, label: "Assigned" },
              { value: 2, label: "Accepted" },
              { value: 4, label: "On hold" },
              { value: 3, label: "Completed" },
              { value: 5, label: "Archived" },
            ]}
          />
        </div>
      )}

      {/* What to do next (display only — no action button) */}
      {nextActionText ? (
        <div className="homeNextAction">
          <span className="homeNextActionLabel">What to do next</span>
          <span className="homeNextActionText">{nextActionText}</span>
        </div>
      ) : (
        <Empty description="No active tasks — you're all caught up" />
      )}

      {/* Grouped by linked visit, each showing the visit's completion % */}
      <div className="homeTaskGroups">
        {groups.map((group) => {
          const completion = visitCompletion.get(group.visitId);
          return (
            <div className="homeTaskGroup" key={group.visitId}>
              <div className="homeTaskGroupHead">
                <Tooltip title={group.visitIdentifier || ""}>
                  <span className="homeTaskGroupTitle">{group.visitName}</span>
                </Tooltip>
                {completion && (
                  <span className="homeTaskGroupProgress">
                    <Progress
                      percent={completion.percent}
                      size="small"
                      strokeColor="#AA1A5F"
                      style={{ width: 120 }}
                    />
                    <span className="homeTaskGroupProgressLabel">
                      {completion.completed}/{completion.total} tasks
                    </span>
                  </span>
                )}
              </div>
              {group.tasks.map(renderTask)}
            </div>
          );
        })}
      </div>

      {sortedTasks.length > PAGE_SIZE && (
        <div className="homeTaskPagination">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={sortedTasks.length}
            onChange={setPage}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}
    </div>
  );
};

export default TaskQueuePanel;
